import { Interval } from 'luxon';
import React, { useState } from 'react';
import styled from 'styled-components';

import { RatingFilterValues } from 'apis/merchant-gateway/insights/reviews/types';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { InfiniteTable } from 'modules/experience-core';

import ErrorPage from '../components/ErrorPage';
import LoadingPage from '../components/LoadingPage';
import { badRatingsFilter } from '../components/ratings/CompensationOffers/utils';
import CompensationOffersCard from '../components/ratings/CompensationOffersCard';
import RatingsBreakdown from '../components/ratings/RatingsBreakdown';
import { RatingsByVenueCard } from '../components/ratings/RatingsByVenueCard';
import { VenueReviewsCard } from '../components/ratings/VenueReviewsCard';
import {
  RatingsByVenueTableColumnIDs,
  RatingsByVenueTableSortableColumnIDs,
} from '../components/ratings/hooks/useRatingsByVenueTableProps';
import {
  VenueReviewsTableColumnIDs,
  VenueReviewsTableSortableColumnIDs,
} from '../components/ratings/hooks/useVenueReviewsTableProps';
import { useRatingsInsightWithPreviousInterval } from '../hooks/useInsightApi';
import useRatingsByVenueInsights from '../hooks/useRatingsByVenueInsights';
import useReviewsInsights from '../hooks/useReviewsInsights';
import { useTelemetryInsightsV2RatingsReviewsView } from '../telemetry';
import { InsightEntity } from '../types';
import { isAllError, isInitialLoading } from '../utils';

type RatingsPageProps = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 16px;
    gap: 16px;
  }
`;

const RatingsAndReviewsPage = ({ insightEntity, dateInterval }: RatingsPageProps) => {
  useTelemetryInsightsV2RatingsReviewsView(insightEntity.type);

  const [venueReviewsSort, setVenueReviewsSort] = useState<InfiniteTable.SortState<VenueReviewsTableSortableColumnIDs>>(
    {
      sortBy: VenueReviewsTableColumnIDs.PURCHASE_DATE,
      sortOrder: InfiniteTable.SortOrder.DESC,
    },
  );
  const [ratingsByVenueSort, setRatingsByVenueSort] = useState<
    InfiniteTable.SortState<RatingsByVenueTableSortableColumnIDs>
  >({
    sortBy: RatingsByVenueTableColumnIDs.AVERAGE_RATING,
    sortOrder: InfiniteTable.SortOrder.ASC,
  });
  const [filters, setFilters] = useState<RatingFilterValues[]>([]);

  const ratingsInsight = useRatingsInsightWithPreviousInterval({
    insightEntity,
    dateInterval,
  });

  const reviewsInsights = useReviewsInsights({
    entityType: insightEntity.type,
    entityId: insightEntity.id,
    interval: dateInterval,
    sortBy: venueReviewsSort?.sortBy,
    sortOrder: venueReviewsSort?.sortOrder,
    filters,
  });
  const ratingsByVenueInsights = useRatingsByVenueInsights({
    entityType: insightEntity.type,
    entityId: insightEntity.id,
    interval: dateInterval,
    sortBy: ratingsByVenueSort?.sortBy,
    sortOrder: ratingsByVenueSort?.sortOrder,
  });

  const allInsightResponses = [ratingsInsight, reviewsInsights, ratingsByVenueInsights];
  if (isInitialLoading(allInsightResponses)) {
    return <LoadingPage />;
  }
  if (isAllError(allInsightResponses)) {
    return <ErrorPage onRetry={ratingsInsight.refetch} />;
  }

  return (
    <Root>
      <RatingsBreakdown ratingsInsight={ratingsInsight} entityType={insightEntity.type} />
      {insightEntity.type === 'venue' && (
        <CompensationOffersCard
          reviewsInsights={reviewsInsights}
          venue={insightEntity.venue}
          onFilterBadReviews={() => setFilters(badRatingsFilter)}
        />
      )}
      {insightEntity.type === 'venue' ? (
        <VenueReviewsCard
          reviewsInsights={reviewsInsights}
          venue={insightEntity.venue}
          sort={venueReviewsSort}
          setSort={setVenueReviewsSort}
          filters={filters}
          setFilters={setFilters}
        />
      ) : (
        <RatingsByVenueCard
          ratingsByVenueInsights={ratingsByVenueInsights}
          sort={ratingsByVenueSort}
          setSort={setRatingsByVenueSort}
        />
      )}
    </Root>
  );
};

export default RatingsAndReviewsPage;
